import React, { useMemo, useState } from 'react';
import { createFaq, deleteFaq, reorderFaqs, updateFaq } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { GuideFaq } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { StatusPill } from '../../../status-pill/status-pill';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { ERROR_COPY, refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import { Counter, H3, Item, List, Muted, Row, Section, SectionHead, Text } from './guide-editor.styled';
import { CODE_IN_TEXT_COPY, moved } from './guide-model';

/**
 * THE FAQS TAB (plan S7.8): up to 50 questions, newest first, in the host's
 * order. Search narrows the list (and hides reordering — a move inside a
 * filtered view would move past rows the host cannot see). Add and Edit share
 * one FormModal; Delete asks first.
 */

type FaqUpdate = (update: (current: GuideFaq[]) => GuideFaq[]) => void;

interface Draft {
  question: string;
  category: string;
  answer: string;
}

const EMPTY: Draft = { question: '', category: '', answer: '' };

export interface FaqManagerProps {
  listingId: string;
  faqs: GuideFaq[];
  limit: number;
  canManage: boolean;
  onFaqsChange: FaqUpdate;
  /** A 409 LIST_CHANGED: the list on screen is stale; read the guide again. */
  onReload: () => void | Promise<void>;
}

export const FaqManager: React.FC<FaqManagerProps> = ({ listingId, faqs, limit, canManage, onFaqsChange, onReload }) => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<GuideFaq | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [initial, setInitial] = useState<Draft>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<GuideFaq | null>(null);
  const [moving, setMoving] = useState(false);

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      q
        ? faqs.filter(f => [f.question, f.answer, f.category || ''].some(s => s.toLowerCase().includes(q)))
        : faqs,
    [faqs, q]
  );
  const full = faqs.length >= limit;

  const open = (faq: GuideFaq | 'new') => {
    const d = faq === 'new' ? EMPTY : { question: faq.question, category: faq.category || '', answer: faq.answer };
    setDraft(d);
    setInitial(d);
    setErrors({});
    setEditing(faq);
  };

  const close = () => {
    if (busy) return;
    setEditing(null);
  };

  const dirty = draft.question !== initial.question || draft.category !== initial.category || draft.answer !== initial.answer;

  const save = async () => {
    if (!editing || busy) return;
    const local: Record<string, string> = {};
    if (!draft.question.trim()) local.question = 'Write the question.';
    if (!draft.answer.trim()) local.answer = 'Write the answer.';
    if (Object.keys(local).length) {
      setErrors(local);

      return;
    }
    setBusy(true);
    setErrors({});
    const body = { question: draft.question.trim(), answer: draft.answer.trim(), category: draft.category.trim() || null };
    try {
      if (editing === 'new') {
        const { faq } = await createFaq(listingId, body);
        onFaqsChange(cur => [faq, ...cur.filter(f => f.id !== faq.id)]);
        toast.success('Question added');
      } else {
        const { faq } = await updateFaq(editing.id, body);
        onFaqsChange(cur => cur.map(f => (f.id === faq.id ? faq : f)));
        toast.success('Question saved');
      }
      setEditing(null);
    } catch (err) {
      const e = err as StaysApiError;
      if (e && e.code === 'CODE_IN_TEXT') {
        const field = e.field || 'answer';
        setErrors({ [field]: CODE_IN_TEXT_COPY });
      } else if (e && e.status === 400 && Object.keys(e.fields || {}).length) {
        setErrors(e.fields);
      } else {
        toast.error(refusalText(e, 'Could not save the question. Try again.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, delta: number) => {
    if (moving) return;
    const next = moved(faqs, index, delta);
    if (next === faqs) return;
    setMoving(true);
    try {
      const out = await reorderFaqs(listingId, next.map(f => f.id));
      onFaqsChange(() => out.faqs);
    } catch (err) {
      const e = err as StaysApiError;
      toast.error(refusalText(e, 'Could not move the question. Try again.'));
      if (e && e.code === 'LIST_CHANGED') void onReload();
    } finally {
      setMoving(false);
    }
  };

  const remove = async (faq: GuideFaq) => {
    try {
      await deleteFaq(faq.id);
      onFaqsChange(cur => cur.filter(f => f.id !== faq.id));
      toast.success('Question deleted');
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not delete the question. Try again.'));
    }
  };

  return (
    <Section aria-label="FAQs">
      <SectionHead>
        <Counter data-testid="faq-count">{`${faqs.length} of ${limit}`}</Counter>
        {canManage ? (
          <ActionWithReason reason={full ? ERROR_COPY.FAQ_LIMIT : null}>
            {reasonId => (
              <AppButton variant="secondary" disabled={full} aria-describedby={reasonId} onClick={() => open('new')}>
                Add a question
              </AppButton>
            )}
          </ActionWithReason>
        ) : null}
      </SectionHead>

      {faqs.length ? (
        <Field>
          <Label htmlFor="faq-search">Search FAQs</Label>
          <Input id="faq-search" type="search" value={query} onChange={e => setQuery(e.target.value)} />
        </Field>
      ) : null}

      {!faqs.length ? (
        <Muted role="status">No questions yet. Add the ones guests ask most.</Muted>
      ) : !shown.length ? (
        <Muted role="status">No questions match your search.</Muted>
      ) : (
        <List aria-label="Questions">
          {shown.map(f => {
            const index = faqs.findIndex(x => x.id === f.id);
            const qid = `faq-q-${f.id}`;

            return (
              <Item key={f.id} data-faq={f.id}>
                {f.category ? (
                  <span>
                    <StatusPill tone="neutral">{f.category}</StatusPill>
                  </span>
                ) : null}
                <H3 id={qid}>{f.question}</H3>
                <Text>{f.answer}</Text>
                {canManage ? (
                  <Row>
                    <AppButton variant="ghost" size="compact" aria-label={`Edit ${f.question}`} onClick={() => open(f)}>
                      Edit
                    </AppButton>
                    <AppButton variant="ghost" size="compact" aria-label={`Delete ${f.question}`} onClick={() => setDeleting(f)}>
                      Delete
                    </AppButton>
                    {!q ? (
                      <>
                        <AppButton variant="ghost" size="compact" aria-describedby={qid} disabled={index <= 0 || moving}
                          onClick={() => void move(index, -1)}>
                          Move up
                        </AppButton>
                        <AppButton variant="ghost" size="compact" aria-describedby={qid}
                          disabled={index >= faqs.length - 1 || moving} onClick={() => void move(index, 1)}>
                          Move down
                        </AppButton>
                      </>
                    ) : null}
                  </Row>
                ) : null}
              </Item>
            );
          })}
        </List>
      )}

      <FormModal
        open={!!editing}
        title={editing === 'new' ? 'Add a question' : 'Edit question'}
        onClose={close}
        isDirty={dirty}
        saving={busy}
        footer={
          <>
            <AppButton variant="ghost" disabled={busy} onClick={close}>
              Cancel
            </AppButton>
            <AppButton variant="primary" loading={busy} loadingLabel="Saving…" onClick={() => void save()}>
              Save question
            </AppButton>
          </>
        }
      >
        <form
          id="faq-form"
          noValidate
          onSubmit={e => {
            e.preventDefault();
            void save();
          }}
        >
          <Field>
            <Label htmlFor="faq-question">Question</Label>
            <Input id="faq-question" value={draft.question} maxLength={200} hasError={!!errors.question}
              aria-describedby={errors.question ? 'faq-question-error' : undefined}
              onChange={e => setDraft({ ...draft, question: e.target.value })} />
            <FieldError id="faq-question-error" message={errors.question} />
          </Field>
          <Field>
            <Label htmlFor="faq-category">Category</Label>
            <Input id="faq-category" value={draft.category} maxLength={40} hasError={!!errors.category}
              aria-describedby={errors.category ? 'faq-category-error' : undefined}
              onChange={e => setDraft({ ...draft, category: e.target.value })} />
            <FieldError id="faq-category-error" message={errors.category} />
          </Field>
          <Field>
            <CountedTextarea id="faq-answer" label="Answer" value={draft.answer} max={2000} rows={6}
              hasError={!!errors.answer} aria-describedby={errors.answer ? 'faq-answer-error' : undefined}
              onChange={e => setDraft({ ...draft, answer: e.target.value })} />
            <FieldError id="faq-answer-error" message={errors.answer} />
          </Field>
        </form>
      </FormModal>

      <ConfirmDialog
        open={!!deleting}
        title={deleting ? `Delete "${deleting.question}"?` : 'Delete question?'}
        message="Guests stop seeing this question at once."
        primaryLabel="Delete question"
        primaryVariant="destructive"
        dismissLabel="Keep question"
        onPrimary={async () => {
          if (deleting) await remove(deleting);
          setDeleting(null);
        }}
        onDismiss={() => setDeleting(null)}
      />
    </Section>
  );
};
